import { useState, useMemo } from 'react';
import { Root } from '@athena/forge';
import Banner from '../common/Banner';
import { useMutationObserver } from '../../hooks/useMutationObserver';

/**
 * Error message mapping for known password input validation errors.
 * Maps Okta error messages to custom display messages.
 */
const ERROR_MESSAGE_MAP: Record<string, string> = {
  'This field cannot be left blank': 'All fields cannot be blank.',
  'New passwords must match': 'New passwords must match.',
};

/**
 * Custom error banner for password reset/expired pages.
 * Watches the Okta error container and password input containers for errors.
 * Maps known input validation errors to custom messages and shows them in the banner.
 * For unknown/server errors, shows the actual error message.
 */
export default function PasswordResetErrorBanner ({
  oktaErrorContainer,
  passwordInputContainers = [],
}: {
  oktaErrorContainer: JQuery<HTMLElement>;
  passwordInputContainers?: JQuery<HTMLElement>[];
}) {
  const getInputErrorMessages = (): string[] => {
    const messages: string[] = [];
    passwordInputContainers.forEach((container) => {
      const errorText = container.find('.okta-form-input-error').text()
        .trim();
      if (errorText && !messages.includes(errorText)) {
        messages.push(errorText);
      }
    });
    return messages;
  };

  const getDisplayMessage = (inputErrors: string[]): string => {
    // Check for mapped error messages from input fields
    for (const inputError of inputErrors) {
      const mapped = ERROR_MESSAGE_MAP[inputError];
      if (mapped) return mapped;
    }
    // If there are input errors but none mapped, show the first one
    if (inputErrors.length > 0) {
      return inputErrors[0];
    }
    return '';
  };

  const hasContainerError = (): boolean => {
    return !!(
      oktaErrorContainer.find('.okta-form-infobox-error').length ||
      oktaErrorContainer.find('.infobox-error').length
    );
  };

  const getContainerErrorText = (): string => {
    const errorBox = oktaErrorContainer.find('.okta-form-infobox-error').length
      ? oktaErrorContainer.find('.okta-form-infobox-error')
      : oktaErrorContainer.find('.infobox-error');
    return errorBox.text().trim();
  };

  const computeState = () => {
    const containerHasError = hasContainerError();
    const inputErrors = getInputErrorMessages();
    const inputDisplayMessage = getDisplayMessage(inputErrors);

    if (containerHasError) {
      // If there are input validation errors, show the mapped message
      // Otherwise show the actual error from the error container (e.g. 500 ISE)
      const message = inputDisplayMessage || getContainerErrorText();
      return { showError: true, message };
    }
    return { showError: false, message: '' };
  };

  const initialState = computeState();
  const [ showError, setShowError ] = useState(initialState.showError);
  const [ displayMessage, setDisplayMessage ] = useState(initialState.message);

  const form = useMemo(() => {
    return oktaErrorContainer.closest('form');
  }, [ oktaErrorContainer ]);

  const updateState = () => {
    const state = computeState();
    setShowError(state.showError);
    setDisplayMessage(state.message);
  };

  const errorContainerMutationArgs = useMemo(() => {
    const filterOptions: MutationObserverInit = { childList: true };
    return {
      filterOptions,
      onMutation: updateState,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ oktaErrorContainer ]);

  useMutationObserver({
    element: oktaErrorContainer[0],
    ...errorContainerMutationArgs,
  });

  // Watch each password input container for error changes
  const inputMutationArgs = useMemo(() => {
    const filterOptions: MutationObserverInit = { childList: true };
    return {
      filterOptions,
      onMutation: updateState,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ passwordInputContainers ]);

  // Observe first input container (new password)
  useMutationObserver({
    element: passwordInputContainers[0]?.length ? passwordInputContainers[0][0] : undefined as unknown as HTMLElement,
    ...inputMutationArgs,
  });

  // Observe second input container (confirm password)
  useMutationObserver({
    element: passwordInputContainers[1]?.length ? passwordInputContainers[1][0] : undefined as unknown as HTMLElement,
    ...inputMutationArgs,
  });

  if (form?.length) {
    form.on('reset', () => {
      setShowError(false);
      setDisplayMessage('');
    });
  }

  if (!showError || !displayMessage) return <></>;

  return (
    <Root>
      <Banner alertType="critical" headerText="Failed to Update Password">
        {displayMessage}
      </Banner>
    </Root>
  );
}
